export const MOVIE = 'movie';
export const TV = 'tv';

// documentary, plus the TV-only talk/news/reality genres. Without these two
// actors who each sat on the same late-night couch read as a shared credit —
// and those shows out-popularity every real overlap
const SKIP_GENRES = new Set([99, 10763, 10764, 10767]);

// only /search/multi and combined_credits tag their entries; everything from a
// movie-specific endpoint is a movie, and only TV carries first_air_date
export function mediaTypeOf(title) {
  if (title.media_type === TV || title.media_type === MOVIE) return title.media_type;
  return title.first_air_date ? TV : MOVIE;
}

// movie ids and TV ids are separate sequences, so the same number means two
// different things — identity has to carry the media type with it
export function uidOf(entity) {
  return entity.uid || `${mediaTypeOf(entity)}-${entity.id}`;
}

export function personUid(person) {
  return person.uid || `person-${person.id}`;
}

export function titleOf(title) {
  return title.title || title.name || '';
}

export function yearOf(title) {
  const date = title.release_date || title.first_air_date;
  return date ? new Date(date).getFullYear() : '';
}

// TMDB's genre catalogue is static, so a lookup table spares a details request;
// movie and TV ids share a namespace without conflicting
const GENRE_NAMES = {
  12: 'Adventure',
  14: 'Fantasy',
  16: 'Animation',
  18: 'Drama',
  27: 'Horror',
  28: 'Action',
  35: 'Comedy',
  36: 'History',
  37: 'Western',
  53: 'Thriller',
  80: 'Crime',
  99: 'Documentary',
  878: 'Science Fiction',
  9648: 'Mystery',
  10402: 'Music',
  10749: 'Romance',
  10751: 'Family',
  10752: 'War',
  10759: 'Action & Adventure',
  10762: 'Kids',
  10763: 'News',
  10764: 'Reality',
  10765: 'Sci-Fi & Fantasy',
  10766: 'Soap',
  10767: 'Talk',
  10768: 'War & Politics',
  10770: 'TV Movie'
};

// the first genre id is the title's primary genre
export function genreOf(title) {
  return GENRE_NAMES[(title.genre_ids || [])[0]] || '';
}

export function isScripted(title) {
  return !(title.genre_ids || []).some(id => SKIP_GENRES.has(id));
}

// awards shows and chat spots come back as ordinary credits billed as "Self" —
// being interviewed isn't being in something
export function isPerformance(credit) {
  return !/^self\b/i.test(credit.character || '');
}

function byPopularity(a, b) {
  return (b.popularity || 0) - (a.popularity || 0);
}

// TMDB scores movie and TV popularity on separate scales, so sorting a mixed
// list by it hands every top slot to one medium. Rank within each medium
// instead, then merge so each takes a share of the lead proportional to how
// much of this filmography it accounts for.
export function rankMixed(credits) {
  const movies = [];
  const shows = [];
  credits.forEach(credit => (mediaTypeOf(credit) === TV ? shows : movies).push(credit));
  if (!movies.length || !shows.length) {
    return [...credits].sort(byPopularity);
  }
  movies.sort(byPopularity);
  shows.sort(byPopularity);

  const merged = [];
  let movieIndex = 0;
  let showIndex = 0;
  while (movieIndex < movies.length || showIndex < shows.length) {
    if (showIndex >= shows.length) {
      merged.push(movies[movieIndex++]);
    } else if (movieIndex >= movies.length) {
      merged.push(shows[showIndex++]);
    } else if ((movieIndex + 1) / movies.length <= (showIndex + 1) / shows.length) {
      // whichever medium has used least of its own list goes next
      merged.push(movies[movieIndex++]);
    } else {
      merged.push(shows[showIndex++]);
    }
  }
  return merged;
}

// aggregate_credits describes a part as roles[{ character, episode_count }]
// where movie credits use a flat character string
export function characterOf(credit) {
  if (credit.character) return credit.character;
  return (credit.roles || []).map(role => role.character).filter(Boolean).join(' · ');
}

// a show's cast comes back in no useful order, so lead the list with whoever
// appeared most — the movie equivalent of TMDB's billing `order`
export function normalizeCast(cast, mediaType) {
  const people = cast.map(person => ({
    ...person,
    uid: personUid(person),
    character: characterOf(person)
  }));
  return mediaType === TV
    ? people.sort((a, b) => (b.total_episode_count || 0) - (a.total_episode_count || 0))
    : people;
}

export function normalizeCredits(credits) {
  return credits.map(credit => ({ ...credit, uid: uidOf(credit) }));
}
